/*
  UserManagement.jsx
  ----------------
  Interface de gestion des groupes et utilisateurs.
*/
import { useEffect, useState, useCallback } from 'react';
import { Button, Modal, TextInput, Checkbox, Group, Table, PasswordInput, Select, Badge } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';
import { get, post } from '../tools/Request';

export default function UserManagement() {
  const navigate = useNavigate();

  const token = localStorage.getItem('userToken');
  const userGroupId = localStorage.getItem('groupId');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const superAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [groupModalIsOpen, setGroupModalIsOpen] = useState(false);

  const [newUser, setNewUser] = useState({ email: '', isAdmin: false, groupId: null });
  const [newGroup, setNewGroup] = useState({ groupName: '', adminEmail: '' });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(superAdmin);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await get('user', null, token);
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchGroups = useCallback(async () => {
    try {
      const data = await get('user/groups', null, token);
      setGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin) navigate('/access-denied');
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, [fetchUsers, fetchGroups]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'isSuperAdmin') setIsSuperAdmin(event.newValue === 'true');
      if (event.key === 'userToken') {
        fetchUsers();
        fetchGroups();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchUsers, fetchGroups]);

  const deactivateUser = async (userToken) => {
    try {
      await post(`user/deactivate/${userToken}`, {}, token);
      await fetchUsers();
      showNotification({ title: 'Succès', message: 'Utilisateur désactivé avec succès', color: 'green' });
    } catch (error) {
      console.error('Error deactivating user:', error);
      showNotification({ title: 'Erreur', message: "Erreur lors de la désactivation de l'utilisateur", color: 'red' });
    }
  };

  const activateUser = async (userToken) => {
    try {
      await post(`user/activate/${userToken}`, {}, token);
      await fetchUsers();
      showNotification({ title: 'Succès', message: 'Utilisateur activé avec succès', color: 'green' });
    } catch (error) {
      console.error('Error activating user:', error);
      showNotification({ title: 'Erreur', message: "Erreur lors de l'activation de l'utilisateur", color: 'red' });
    }
  };

  const createUser = async () => {
    try {
      const user = { ...newUser };
      if (!isSuperAdmin) user.groupId = userGroupId;

      await post('user', user, token);
      setModalIsOpen(false);
      setNewUser({ email: '', isAdmin: false, groupId: null });
      fetchUsers();
      showNotification({ title: 'Succès', message: 'Utilisateur créé avec succès', color: 'green' });
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification({ title: 'Erreur', message: error?.error || 'Erreur lors de la création', color: 'red' });
    }
  };

  const createGroup = async () => {
    try {
      await post('user/group', newGroup, token);
      setGroupModalIsOpen(false);
      setNewGroup({ groupName: '', adminEmail: '' });
      fetchUsers();
      fetchGroups();
      showNotification({ title: 'Succès', message: 'Groupe créé avec succès', color: 'green' });
    } catch (error) {
      console.error('Error creating group:', error);
      showNotification({ title: 'Erreur', message: 'Erreur lors de la création du groupe', color: 'red' });
    }
  };

  const filteredUsers = selectedGroup ? users.filter((u) => u.group_name === selectedGroup) : users;

  return (
    <div>
      <h1>Gestion des utilisateurs</h1>

      <Group position="right" mb={20}>
        {isSuperAdmin && (
          <Button variant="light" onClick={() => setGroupModalIsOpen(true)}>
            Créer un nouveau groupe
          </Button>
        )}
        <Button variant="light" onClick={() => setModalIsOpen(true)}>
          Créer un nouvel utilisateur
        </Button>
      </Group>

      {isSuperAdmin && (
        <Select
          label="Filtrer par groupe"
          placeholder="Sélectionnez un groupe"
          maw={400}
          value={selectedGroup}
          onChange={(value) => setSelectedGroup(value)}
          data={groups.map((g) => ({ value: g.name, label: g.name }))}
          mb={20}
        />
      )}

      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Identifiant</Table.Th>
            <Table.Th>Rôle</Table.Th>
            {isSuperAdmin && <Table.Th>Groupe</Table.Th>}
            <Table.Th>Statut</Table.Th>
            <Table.Th>Token</Table.Th>
            <Table.Th>Clé publique</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {filteredUsers.map((user) => {
            const isSelf = token === user.token;
            const actif = user.actif === true;

            return (
              <Table.Tr key={user.token}>
                <Table.Td>{user.email}</Table.Td>
                <Table.Td>{user.issuperadmin ? 'Super Administrateur' : user.isadmin ? 'Administrateur' : 'Utilisateur'}</Table.Td>

                {isSuperAdmin && <Table.Td>{user.group_name || 'Aucun groupe'}</Table.Td>}

                <Table.Td>{actif ? <Badge color="green">Actif</Badge> : <Badge color="red">Inactif</Badge>}</Table.Td>

                <Table.Td>
                  <PasswordInput value={user.token} readOnly style={{ width: '100%' }} />
                </Table.Td>

                <Table.Td>
                  <PasswordInput value={user.publickey} readOnly style={{ width: '100%' }} />
                </Table.Td>

                <Table.Td>
                  {!isSelf && (
                    <Group gap="xs">
                      <Button variant="light" color="green" disabled={actif} onClick={() => activateUser(user.token)}>
                        Activer
                      </Button>

                      <Button variant="light" color="red" disabled={!actif} onClick={() => deactivateUser(user.token)}>
                        Désactiver
                      </Button>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {/* Modal - Créer un utilisateur */}
      <Modal opened={modalIsOpen} onClose={() => setModalIsOpen(false)} title="Créer un nouvel utilisateur">
        <TextInput label="Identifiant" placeholder="Identifiant" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />

        {isSuperAdmin && (
          <>
            <Checkbox mt="md" label="Administrateur" checked={newUser.isAdmin} onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })} />
            <Select
              label="Groupe"
              placeholder="Sélectionnez un groupe"
              value={newUser.groupId}
              onChange={(value) => setNewUser({ ...newUser, groupId: value })}
              data={groups.map((g) => ({ value: g.id.toString(), label: g.name }))}
            />
          </>
        )}

        <Group mt="md" position="right">
          <Button onClick={createUser}>Créer</Button>
          <Button variant="outline" onClick={() => setModalIsOpen(false)}>
            Annuler
          </Button>
        </Group>
      </Modal>

      {/* Modal - Créer un groupe */}
      {isSuperAdmin && (
        <Modal opened={groupModalIsOpen} onClose={() => setGroupModalIsOpen(false)} title="Créer un nouveau groupe">
          <TextInput label="Nom du groupe" placeholder="Nom du groupe" value={newGroup.groupName} onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })} />
          <TextInput
            label="Identifiant de l'admin"
            placeholder="Identifiant de l'admin"
            value={newGroup.adminEmail}
            onChange={(e) => setNewGroup({ ...newGroup, adminEmail: e.target.value })}
          />

          <Group mt="md" position="right">
            <Button onClick={createGroup}>Créer</Button>
            <Button variant="outline" onClick={() => setGroupModalIsOpen(false)}>
              Annuler
            </Button>
          </Group>
        </Modal>
      )}
    </div>
  );
}
